/**
 * M9 — Conformidade.
 *
 * Réplica frontend-only do módulo M9 do protótipo V5 (docs/reference/
 * GeoGuard_UPM_v5_4_4.html, renderM9). Recomendações da auditoria
 * (RISR 1S/2026) com prazo + detalhe expansível, e obrigações
 * recorrentes dos órgãos fiscalizadores (ANM/FEAM).
 *
 * POC de visualização para reunião — dados mockados inline, sem
 * datasource/Supabase. Quando integrar (Galvani + JFB, PAEBM digital +
 * API SIGBM), trocar MOCK_RECS/MOCK_OBRIGACOES por um DataSource.
 */
import { useMemo, useState } from 'react';
import { Card, CardTitle } from '@/ui';

type Criticidade = 'ALERTA' | 'Rotina';
type StatusRec = 'Em andamento' | 'Pendente';

interface Recomendacao {
  id: string;
  crit: Criticidade;
  status: StatusRec;
  /** ISO date (YYYY-MM-DD) do prazo. */
  deadline: string;
  title: string;
  detail: string;
  action: string;
  ref: string;
}

interface Obrigacao {
  id: string;
  org: 'ANM' | 'FEAM' | 'ANM/FEAM';
  label: string;
  freq: string;
  proxima: string;
  status: 'ok' | 'warn';
  obs?: string;
}

// Recomendações da Auditoria — JF Brasil / RISR 1S/2026 (protótipo V5).
const MOCK_RECS: Recomendacao[] = [
  {
    id: '#1', crit: 'Rotina', status: 'Em andamento', deadline: '2026-06-30',
    title: 'Monitoramento contínuo (horário) das precipitações e NA do reservatório',
    detail:
      'Fonte: Auditoria Extraordinária 2º Sem/2025 (MOS-24004144-RTL-002, JF Brasil). Recomendação de ' +
      'monitoramento contínuo (horário) das precipitações e NA do reservatório para atualização periódica ' +
      'da calibração do CN (Curve Number), em atendimento ao §5, Art. 24 da Resolução ANM nº 95/2022. ' +
      'CN calibrado adotado = 39. Área de contribuição = 2,97 km². Status: em andamento como rotina ' +
      'operacional do monitoramento automático.',
    action: 'Manter monitoramento horário automatizado. Validar calibração do CN periodicamente com dados acumulados.',
    ref: 'MOS-24004144-RTL-002 · Res. ANM 95/2022 Art.24 §5',
  },
  {
    id: '#2', crit: 'ALERTA', status: 'Pendente', deadline: '2026-06-30',
    title: 'Verificar calibração do instrumento de medição do NA do reservatório',
    detail:
      'Fonte: RISR 1S/2026 Tabela 11.1 (JF Brasil, 2026). Há indício de descalibração do instrumento ' +
      'automático de medição do Nível d\'Água (NA) do reservatório da Barragem B. As leituras realizadas ' +
      'entre janeiro de 2025 e a data mais recente anterior à calibração podem estar comprometidas. É ' +
      'necessário verificar a calibração e validar as medidas do período indicado. Impacto: leituras do ' +
      'NA do reservatório com flag de calibração pendente no sistema.',
    action:
      'Contratar calibração do instrumento NA do reservatório. Validar todas as leituras jan/2025–data da ' +
      'calibração. Atualizar carta de controle após validação.',
    ref: 'RISR 1S/2026 Tabela 11.1 — Prazo: 30/06/2026',
  },
  {
    id: '#3', crit: 'ALERTA', status: 'Pendente', deadline: '2026-05-31',
    title: 'Verificar leituras automatizadas do NA do reservatório vs. leituras topográficas',
    detail:
      'Fonte: RISR 1S/2026 Tabela 11.1. Necessidade de confrontar as leituras automatizadas do NA do ' +
      'reservatório com leituras topográficas independentes para verificar a consistência dos dados. Esta ' +
      'verificação é distinta da calibração (#2) e foca na comparação entre o instrumento automático e o ' +
      'referencial topográfico da barragem. Prazo mais urgente: 31/05/2026.',
    action:
      'Realizar leitura topográfica do NA do reservatório. Comparar com série automática. Documentar ' +
      'divergências e corrigir offset se identificado.',
    ref: 'RISR 1S/2026 Tabela 11.1 — Prazo: 31/05/2026',
  },
  {
    id: '#4', crit: 'ALERTA', status: 'Pendente', deadline: '2027-06-30',
    title: 'Atualizar estudo de chuvas para TR 10.000 anos e PMP',
    detail:
      'Fonte: RISR 1S/2026 Tabela 11.1. Recomendação de atualização do estudo hidrológico da Barragem B ' +
      'para os períodos de retorno de 10.000 anos (TR 10.000a) e Precipitação Máxima Provável (PMP), ' +
      'conforme exigência da Resolução ANM nº 95/2022. O estudo atual foi elaborado com base em topografia ' +
      'anterior — a batimetria CCC Topografia 2025 já foi atualizada, permitindo revisão dos parâmetros. O ' +
      'sistema extravasor existente já atende à PMP atual, conforme avaliado no RISR 2026.',
    action:
      'Contratar elaboração de estudo hidrológico atualizado (TR 10.000 anos e PMP). Usar topografia CCC 2025 ' +
      'e batimetria atualizada.',
    ref: 'RISR 1S/2026 Tabela 11.1 · Res. ANM 95/2022 — Prazo: 30/06/2027',
  },
];

const MOCK_OBRIGACOES: Obrigacao[] = [
  { id: 'OR-01', org: 'ANM', label: 'ISR Quinzenal — Extrato SIGBM', freq: 'Quinzenal', proxima: '28/04/2026', status: 'ok' },
  { id: 'OR-02', org: 'ANM', label: 'RISR Semestral — Cadastro SIGBM', freq: 'Semestral', proxima: '30/06/2026', status: 'warn', obs: '1º ciclo: entre 01/03 e 31/03' },
  { id: 'OR-03', org: 'ANM', label: 'Declaração de Condição de Estabilidade', freq: 'Semestral', proxima: '30/06/2026', status: 'warn', obs: 'Junto ao RISR' },
  { id: 'OR-04', org: 'ANM', label: 'Cadastro ANM — Res. 220/2025 (SIGBM)', freq: 'Anual', proxima: '30/06/2026', status: 'warn' },
  { id: 'OR-05', org: 'FEAM', label: 'RTESB — Relatório Técnico Estabilidade', freq: 'Anual', proxima: '30/09/2026', status: 'ok', obs: 'Conforme TR FEAM/MG' },
  { id: 'OR-06', org: 'FEAM', label: 'Comunicado de Anomalia à FEAM (quando aplicável)', freq: 'Eventual', proxima: '—', status: 'ok' },
  { id: 'OR-07', org: 'ANM/FEAM', label: 'RPSB — Revisão Periódica (DPA Médio)', freq: 'A cada 5 anos', proxima: '2027', status: 'ok', obs: 'DPA = Médio' },
];

const ORG_COLOR: Record<Obrigacao['org'], string> = {
  ANM: '#3b82f6',
  FEAM: '#8b5cf6',
  'ANM/FEAM': '#06b6d4',
};

/** Dias até o prazo, relativo a hoje. */
function daysLeft(deadline: string): number {
  const ms = new Date(deadline).getTime() - Date.now();
  return Math.ceil(ms / 86_400_000);
}

/** Cor do contador de prazo — espelha urgColor do protótipo. */
function urgColor(days: number): string {
  if (days <= 30) return '#ef4444'; // vencida ou crítica (≤30d)
  if (days <= 60) return '#f97316'; // atenção (≤60d)
  return '#FECB00'; // confortável
}

function RecCard({ rec }: { rec: Recomendacao }) {
  const [open, setOpen] = useState(false);
  const days = useMemo(() => daysLeft(rec.deadline), [rec.deadline]);
  const isAlerta = rec.crit === 'ALERTA';
  const isAndamento = rec.status === 'Em andamento';
  const daysLabel = days < 0 ? 'VENCIDA' : `${days}d`;

  return (
    <div
      className="bg-jf-surface border rounded-xl p-4 mb-2.5"
      style={{ borderColor: isAlerta ? 'rgba(249,115,22,.35)' : 'rgba(254,203,0,.25)' }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-3 text-left"
        aria-expanded={open}
      >
        <div className="flex-1">
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            <span className="text-[10px] font-bold px-2 py-0.5 rounded border text-jf-yellow border-jf-yellow/30 bg-jf-yellow/10">
              {rec.id}
            </span>
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded border"
              style={
                isAlerta
                  ? { color: '#f97316', background: 'rgba(249,115,22,.1)', borderColor: 'rgba(249,115,22,.3)' }
                  : { color: '#eab308', background: 'rgba(234,179,8,.1)', borderColor: 'rgba(234,179,8,.3)' }
              }
            >
              {rec.crit}
            </span>
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded border"
              style={
                isAndamento
                  ? { color: '#22c55e', background: 'rgba(34,197,94,.1)', borderColor: 'rgba(34,197,94,.3)' }
                  : { color: '#eab308', background: 'rgba(234,179,8,.1)', borderColor: 'rgba(234,179,8,.3)' }
              }
            >
              {rec.status}
            </span>
          </div>
          <div className="text-sm font-semibold text-white">{rec.title}</div>
          <div className="text-[11px] text-jf-muted mt-0.5">
            Prazo: {new Date(rec.deadline).toLocaleDateString('pt-BR')} · Ref.: {rec.ref || '—'}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-2xl font-black leading-none" style={{ color: urgColor(days) }}>
            {daysLabel}
          </div>
          <div className="text-[10px] text-jf-dim">faltam</div>
          <div className="text-[10px] text-jf-dim mt-1">{open ? '▲ fechar' : '▼ detalhes'}</div>
        </div>
      </button>

      {open && (
        <div className="mt-3 pt-3 border-t border-jf-border">
          <p className="text-xs text-slate-200 leading-relaxed mb-2">{rec.detail}</p>
          <div className="rounded-lg p-2.5 mt-2 border border-jf-yellow/20 bg-jf-yellow/5">
            <p className="text-[10px] text-jf-yellow font-bold mb-1">AÇÃO RECOMENDADA:</p>
            <p className="text-xs text-slate-200">{rec.action || '—'}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default function M9Conformidade() {
  const [obrigacoes, setObrigacoes] = useState<Obrigacao[]>(MOCK_OBRIGACOES);

  return (
    <div>
      <header className="mb-4">
        <h1 className="text-2xl font-black text-jf-yellow">📋 Conformidade</h1>
        <p className="text-jf-muted text-sm mt-1">RISR 1S/2026 e obrigações regulatórias (ANM / FEAM)</p>
      </header>

      <p className="text-[11px] text-jf-dim uppercase tracking-wider mb-2.5">
        Recomendações da Auditoria — JF Brasil / 1S 2026
      </p>
      <div className="mb-5">
        {MOCK_RECS.map((rec) => (
          <RecCard key={rec.id} rec={rec} />
        ))}
      </div>

      <Card>
        <CardTitle>Obrigações recorrentes — órgãos fiscalizadores (ANM / FEAM)</CardTitle>
        {obrigacoes.length === 0 ? (
          <p className="text-jf-muted text-sm py-2.5">Nenhuma obrigação cadastrada.</p>
        ) : (
          <ul>
            {obrigacoes.map((o) => {
              const c = ORG_COLOR[o.org];
              return (
                <li key={o.id} className="flex items-center gap-2.5 py-2.5 border-b border-jf-border last:border-0">
                  <span
                    className="shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full border"
                    style={{ background: `${c}22`, color: c, borderColor: `${c}55` }}
                  >
                    {o.org}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white">{o.label}</div>
                    <div className="text-[10px] text-jf-dim mt-0.5">
                      {o.freq}{o.obs ? ` · ${o.obs}` : ''}
                    </div>
                  </div>
                  <span className="font-mono text-[11px] text-jf-muted shrink-0">{o.proxima}</span>
                  <span
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ background: o.status === 'ok' ? '#22c55e' : '#FECB00' }}
                    aria-label={o.status === 'ok' ? 'em dia' : 'atenção'}
                  />
                  <button
                    type="button"
                    onClick={() => setObrigacoes((prev) => prev.filter((x) => x.id !== o.id))}
                    className="text-jf-dim hover:text-level-emergencia text-sm px-1 shrink-0"
                    aria-label={`Remover ${o.label}`}
                  >
                    ×
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
